import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError, imgUrl } from "@/lib/api";
import { toast } from "sonner";
import { AutomationsSubnav, PageIntro } from "@/components/SellerToolsNav";

function Shell({ kicker, title, blurb, children, actions }) {
  return (
    <div className="max-w-[1100px] mx-auto px-6 lg:px-10 pt-28 pb-24">
      <AutomationsSubnav />
      <PageIntro kicker={kicker} title={title} blurb={blurb} actions={actions} />
      {children}
    </div>
  );
}

function Hub() {
  const { data } = useQuery({
    queryKey: ["seller-automations"],
    queryFn: async () => (await api.get("/seller/automations")).data,
  });
  const counts = data?.counts || {};
  const cards = [
    { to: "/seller/offers", title: "Send Offers", desc: "Pick listings, set a discount, apply it on Shoptosa or copy a pack for eBay, Poshmark, Mercari, Depop.", count: null },
    { to: "/seller/automations/auto-offers", title: "Auto Offers", desc: "Queue offers for listings older than N days. Native shop discounts apply here. Other sites stay copy packs.", count: counts.auto_offers },
    { to: "/seller/automations/sharing", title: "Marketplace Sharing", desc: "Due-to-share queue with bump copy. We never click Share on Poshmark, Depop, or Grailed.", count: counts.share_due },
    { to: "/seller/automations/stale", title: "Stale Listing Warning", desc: "Yellow flag on inventory after 15, 30, 60, or 90 days from the newest listing date.", count: counts.stale },
  ];
  return (
    <Shell kicker="Automations" title="Keep listings moving" blurb="Offers, sharing reminders, and stale flags live here — grouped the way a multichannel seller works, without unofficial marketplace posting.">
      <div className="grid sm:grid-cols-2 gap-4" data-testid="automations-hub">
        {cards.map((c) => (
          <Link key={c.to} to={c.to} className="bg-aura-surface border border-aura-border rounded-xl p-5 hover:border-aura-accent">
            <div className="font-medium">{c.title}</div>
            <p className="text-sm text-aura-muted mt-2">{c.desc}</p>
            {c.count != null && <div className="text-xs font-mono text-aura-cyan mt-3">{c.count} due</div>}
          </Link>
        ))}
      </div>
    </Shell>
  );
}

function AutoOffers() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["seller-automations"],
    queryFn: async () => (await api.get("/seller/automations")).data,
  });
  const { data: offers } = useQuery({
    queryKey: ["seller-offers"],
    queryFn: async () => (await api.get("/seller/offers")).data,
  });
  const auto = data?.automations?.auto_offers || {};
  const [form, setForm] = useState(null);
  const cfg = form || auto;
  const due = (offers?.candidates || []).filter((p) => p.auto_offer_due);

  const save = async () => {
    try {
      await api.put("/seller/automations/auto-offers", cfg);
      toast.success("Auto Offers saved");
      qc.invalidateQueries({ queryKey: ["seller-automations"] });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const run = async () => {
    try {
      const { data: res } = await api.post("/seller/automations/auto-offers/run");
      toast.success(`${res.offers.length} offer${res.offers.length === 1 ? "" : "s"} queued`);
      qc.invalidateQueries({ queryKey: ["seller-offers"] });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Shell
      kicker="Automations"
      title="Auto Offers"
      blurb="When a listing is older than your threshold, queue a discount. Shoptosa shop prices update. Other marketplaces get a message to paste — we do not send liker offers on those sites."
    >
      <div className="bg-aura-surface border border-aura-border rounded-xl p-5 space-y-4 mb-8">
        <label className="flex items-center justify-between gap-4 text-sm">
          Enabled
          <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setForm({ ...cfg, enabled: e.target.checked })} />
        </label>
        <label className="text-sm block">
          Discount %
          <input type="number" min="1" max="80" value={cfg.discount_pct || 10} onChange={(e) => setForm({ ...cfg, discount_pct: Number(e.target.value) })} className="mt-1 w-full px-3 py-2 border border-aura-border rounded-lg bg-aura-surface" />
        </label>
        <label className="text-sm block">
          Minimum listing age (days)
          <input type="number" min="1" value={cfg.min_age_days || 7} onChange={(e) => setForm({ ...cfg, min_age_days: Number(e.target.value) })} className="mt-1 w-full px-3 py-2 border border-aura-border rounded-lg bg-aura-surface" />
        </label>
        <div className="flex gap-2">
          <button type="button" onClick={save} className="px-4 py-2 bg-aura-accent text-white text-sm rounded-lg">Save rules</button>
          <button type="button" onClick={run} className="px-4 py-2 border border-aura-border text-sm rounded-lg">Run now</button>
        </div>
      </div>
      <div className="text-sm text-aura-muted mb-3">{due.length} listing{due.length === 1 ? "" : "s"} match the current rules</div>
      <div className="space-y-2">
        {due.slice(0, 20).map((p) => (
          <div key={p.id} className="flex items-center gap-3 text-sm">
            <img src={imgUrl(p.images?.[0])} alt="" className="w-10 h-12 object-cover rounded" />
            <span className="truncate">{p.title}</span>
            <span className="text-aura-muted font-mono">{p.age_days}d</span>
          </div>
        ))}
      </div>
    </Shell>
  );
}

function Sharing() {
  const qc = useQueryClient();
  const { data: auto } = useQuery({
    queryKey: ["seller-automations"],
    queryFn: async () => (await api.get("/seller/automations")).data,
  });
  const { data } = useQuery({
    queryKey: ["share-due"],
    queryFn: async () => (await api.get("/seller/automations/sharing/due")).data,
  });
  const settings = data?.settings || auto?.automations?.sharing || {};
  const [form, setForm] = useState(null);
  const cfg = form || settings;

  const save = async () => {
    try {
      await api.put("/seller/automations/sharing", cfg);
      toast.success("Sharing reminders saved");
      qc.invalidateQueries({ queryKey: ["seller-automations"] });
      qc.invalidateQueries({ queryKey: ["share-due"] });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const copy = async (text, id) => {
    await navigator.clipboard.writeText(text);
    try {
      await api.post("/seller/automations/sharing/mark", { product_ids: [id], channel: (cfg.channels || ["poshmark"])[0] });
      qc.invalidateQueries({ queryKey: ["share-due"] });
    } catch { /* still copied */ }
    toast.success("Copied — paste on that marketplace, then we mark it shared");
  };

  return (
    <Shell
      kicker="Automations"
      title="Marketplace Sharing"
      blurb="A due queue for bumping Poshmark, Depop, and Grailed. Copy the pack, share yourself, mark done. We do not drive those UIs."
    >
      <div className="bg-aura-surface border border-aura-border rounded-xl p-5 space-y-4 mb-8">
        <label className="flex items-center justify-between gap-4 text-sm">
          Reminders on
          <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setForm({ ...cfg, enabled: e.target.checked })} />
        </label>
        <label className="text-sm block">
          Interval (days)
          <input type="number" min="1" max="30" value={cfg.interval_days || 7} onChange={(e) => setForm({ ...cfg, interval_days: Number(e.target.value) })} className="mt-1 w-full px-3 py-2 border border-aura-border rounded-lg bg-aura-surface" />
        </label>
        <button type="button" onClick={save} className="px-4 py-2 bg-aura-accent text-white text-sm rounded-lg">Save</button>
      </div>
      {(data?.items || []).length === 0 ? (
        <div className="text-sm text-aura-muted">Nothing is due. Connect a channel, mark it listed, then wait for the interval.</div>
      ) : (
        <div className="space-y-4">
          {(data.items || []).map((p) => (
            <div key={p.id} className="border border-aura-border rounded-lg p-4">
              <div className="font-medium">{p.title}</div>
              {Object.entries(p.share_copy || {}).map(([cid, text]) => (
                <button key={cid} type="button" onClick={() => copy(text, p.id)} className="mt-2 mr-2 text-xs px-3 py-1.5 border border-aura-border rounded-lg">
                  Copy {cid}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </Shell>
  );
}

function Stale() {
  const qc = useQueryClient();
  const { data: auto } = useQuery({
    queryKey: ["seller-automations"],
    queryFn: async () => (await api.get("/seller/automations")).data,
  });
  const { data } = useQuery({
    queryKey: ["stale-items"],
    queryFn: async () => (await api.get("/seller/automations/stale/items")).data,
  });
  const settings = data?.settings || auto?.automations?.stale || { days: 60, enabled: true };
  const options = auto?.stale_days_options || [15, 30, 60, 90];

  const save = async (patch) => {
    try {
      await api.put("/seller/automations/stale", patch);
      toast.success("Stale warning saved");
      qc.invalidateQueries({ queryKey: ["seller-automations"] });
      qc.invalidateQueries({ queryKey: ["stale-items"] });
      qc.invalidateQueries({ queryKey: ["crosslist-items"] });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Shell
      kicker="Automations"
      title="Stale Listing Warning"
      blurb="In inventory we show a warning after a number of days. The most recent listing date across marketplaces is used."
    >
      <div className="bg-aura-surface border border-aura-border rounded-xl p-5 mb-8">
        <div className="font-medium mb-3">Display a warning message on your items after:</div>
        <select
          value={settings.days}
          onChange={(e) => save({ days: Number(e.target.value), enabled: true })}
          data-testid="stale-days"
          className="px-3 py-2 border border-aura-border rounded-lg bg-aura-surface"
        >
          {options.map((d) => <option key={d} value={d}>{d} days</option>)}
        </select>
        <p className="text-xs text-aura-muted mt-3">The most recent listing date across marketplaces determines if the item is stale.</p>
      </div>
      <div className="text-sm text-aura-muted mb-3">{(data?.items || []).length} stale listing{(data?.items || []).length === 1 ? "" : "s"}</div>
      <div className="space-y-2">
        {(data?.items || []).map((p) => (
          <Link key={p.id} to="/seller/crosslist" className="flex items-center gap-3 text-sm">
            <span className="text-amber-400">⚠</span>
            <img src={imgUrl(p.images?.[0])} alt="" className="w-10 h-12 object-cover rounded" />
            <span className="truncate">{p.title}</span>
            <span className="font-mono text-aura-muted">{p.age_days}d</span>
          </Link>
        ))}
      </div>
    </Shell>
  );
}

export default function Automations() {
  const { pathname } = useLocation();
  if (pathname.endsWith("/auto-offers")) return <AutoOffers />;
  if (pathname.endsWith("/sharing")) return <Sharing />;
  if (pathname.endsWith("/stale")) return <Stale />;
  return <Hub />;
}
